import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import api from '../../config/axiosConfig';

const BranchManagementModal = ({ open, onClose, quotation, onSuccess }) => {
  const [applicabilityScope, setApplicabilityScope] = useState('MATRIX_ONLY');
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [companyBranches, setCompanyBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Cargar sucursales de la empresa y datos actuales de la cotización
  useEffect(() => {
    if (open && quotation) {
      loadBranchesAndQuotationData();
    }
  }, [open, quotation]);

  const loadBranchesAndQuotationData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Cargar alcance actual de la cotización
      setApplicabilityScope(quotation.applicabilityScope || 'MATRIX_ONLY');
      
      // Si la cotización tiene empresa, cargar sus sucursales
      if (quotation.companyId) {
        const branchesResponse = await api.get(`/company-branches/company/${quotation.companyId}`);
        
        // Debug: Ver todas las sucursales y su estado
        console.log('🔍 DEBUG - Todas las sucursales:', branchesResponse.data.map(b => ({
          id: b.id,
          name: b.branchName,
          isActive: b.isActive,
          tipo: typeof b.isActive
        })));
        
        // Filtrar solo sucursales activas (verificar tanto true como 1)
        const activeBranches = branchesResponse.data.filter(branch => 
          branch.isActive === true || branch.isActive === 1 || branch.isActive === '1'
        );
        setCompanyBranches(activeBranches);
        
        console.log(`🏢 Sucursales activas cargadas: ${activeBranches.length} de ${branchesResponse.data.length} totales`);
        
        // Cargar sucursales seleccionadas actualmente (solo las que siguen activas)
        if (quotation.branchIds && quotation.branchIds.length > 0) {
          const activeSelectedBranches = quotation.branchIds.filter(branchId => 
            activeBranches.some(branch => branch.id === branchId)
          );
          setSelectedBranches(activeSelectedBranches);
        } else {
          setSelectedBranches([]);
        }
      }
    } catch (err) {
      console.error('Error al cargar datos:', err);
      setError('Error al cargar sucursales de la empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleScopeChange = (event) => {
    const newScope = event.target.value;
    setApplicabilityScope(newScope);
    
    // Si cambia a MATRIX_ONLY o ALL_BRANCHES, limpiar selección específica
    if (newScope !== 'SPECIFIC_BRANCHES') {
      setSelectedBranches([]);
    }
  };

  const handleBranchToggle = (branchId) => {
    setSelectedBranches(prev => {
      if (prev.includes(branchId)) {
        return prev.filter(id => id !== branchId);
      } else {
        return [...prev, branchId];
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const response = await api.put(`/quotations/${quotation.id}/branches`, {
        applicabilityScope,
        branchIds: applicabilityScope === 'SPECIFIC_BRANCHES' ? selectedBranches : []
      });
      
      console.log('✅ Sucursales actualizadas:', response.data);
      
      // Llamar callback de éxito
      if (onSuccess) {
        onSuccess(response.data.quotation);
      }
      
      // Cerrar modal
      onClose();
    } catch (err) {
      console.error('❌ Error al actualizar sucursales:', err);
      setError(err.response?.data?.message || 'Error al actualizar sucursales');
    } finally {
      setSaving(false);
    }
  };

  const calculateEntities = () => {
    if (applicabilityScope === 'MATRIX_ONLY') {
      return 1;
    } else if (applicabilityScope === 'ALL_BRANCHES') {
      return 1 + companyBranches.length;
    } else if (applicabilityScope === 'SPECIFIC_BRANCHES') {
      return selectedBranches.length;
    }
    return 1;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <BusinessIcon color="primary" />
          <Typography variant="h6">Gestionar Sucursales</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Cotización: {quotation?.invoiceFolio}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold' }}>
                Alcance de la Cotización
              </FormLabel>
              <RadioGroup value={applicabilityScope} onChange={handleScopeChange}>
                <FormControlLabel
                  value="MATRIX_ONLY"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">Solo Matriz</Typography>
                      <Typography variant="caption" color="text.secondary">
                        La cotización aplica únicamente a la empresa matriz
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="ALL_BRANCHES"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">Todas las Sucursales</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Incluye matriz + todas las sucursales activas ({companyBranches.length} sucursales)
                      </Typography>
                    </Box>
                  }
                  disabled={companyBranches.length === 0}
                />
                <FormControlLabel
                  value="SPECIFIC_BRANCHES"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">Sucursales Específicas</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Selecciona manualmente las sucursales a incluir
                      </Typography>
                    </Box>
                  }
                  disabled={companyBranches.length === 0}
                />
              </RadioGroup>
            </FormControl>

            {applicabilityScope === 'SPECIFIC_BRANCHES' && companyBranches.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Seleccionar Sucursales:
                </Typography>
                <FormGroup>
                  {companyBranches.map((branch) => (
                    <FormControlLabel
                      key={branch.id}
                      control={
                        <Checkbox
                          checked={selectedBranches.includes(branch.id)}
                          onChange={() => handleBranchToggle(branch.id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">{branch.branchName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {branch.fullAddress || branch.address}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>
              </Box>
            )}

            <Box sx={{ mt: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                📊 Resumen:
              </Typography>
              <Typography variant="body2">
                Número de entes: <strong>{calculateEntities()}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                El monto total se recalculará automáticamente basándose en el nuevo número de entes.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving || (applicabilityScope === 'SPECIFIC_BRANCHES' && selectedBranches.length === 0)}
          startIcon={saving && <CircularProgress size={20} />}
        >
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BranchManagementModal;
